import React, { useState, useEffect, useRef } from 'react';
import styled from 'styled-components';
import Typography from '@mui/material/Typography';
import Tooltip from '@mui/material/Tooltip';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import CancelIcon from '@mui/icons-material/Cancel';
import RemoveCircleOutlineIcon from '@mui/icons-material/RemoveCircleOutline';
import ErrorIcon from '@mui/icons-material/Error';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import DeleteIcon from '@mui/icons-material/Delete';
import CloseIcon from '@mui/icons-material/Close';
import AddIcon from '@mui/icons-material/Add';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import RefreshIcon from '@mui/icons-material/Refresh';
import CircularProgress from '@mui/material/CircularProgress';
import Collapse from '@mui/material/Collapse';
import Dialog from '@mui/material/Dialog';
import DialogTitle from '@mui/material/DialogTitle';
import DialogContent from '@mui/material/DialogContent';
import DialogActions from '@mui/material/DialogActions';
import Button from '@mui/material/Button';
import IconButton from '@mui/material/IconButton';
import Checkbox from '@mui/material/Checkbox';
import MenuItem from '@mui/material/MenuItem';
import Select from '@mui/material/Select';
import Box from '@mui/material/Box';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableContainer from '@mui/material/TableContainer';
import TableHead from '@mui/material/TableHead';
import MuiTableRow from '@mui/material/TableRow';
import Paper from '@mui/material/Paper';
import TextField from '@mui/material/TextField';
import { Application } from '@feathersjs/feathers';
import { useFeathers } from '../../app/util';
import { ServiceTypes } from '../../app/feathers/ServiceTypes';
import { SampleBallotRaw, ParseStatus, PotentialContentType, PotentialSampleBallot, AutoScouter, PatchAutoScouter } from '../../app/feathers/auto-scouter/AutoScouter';
import { Election } from '../../app/feathers/elections/Election';
import PromptEditor from '../PromptEditor';
import { PARSE_SAMPLE_BALLOT_INSTRUCTIONS } from './parseSampleBallotInstructions';

interface SampleBallotTileProps {
  url: string;
  parseStatus: ParseStatus;
  value: SampleBallotRaw;
  relevant?: boolean;
  enableEditing?: boolean;
  disableEditing?: boolean;
  autoScouterId: string;
  sourceType?: PotentialContentType;
  bodyText?: string;
  errorMessage?: string | null;
  warningMessage?: string | null;
   // Last prompt used when this item was parsed (if any), passed from parent
  promptLastUsed?: string;
  bulkEditMode?: boolean;
  isSelected?: boolean;
  onSelectionChange?: (selected: boolean) => void;
  onRemove?: () => void;
  removing?: boolean;
  onChangeBallot?: (sampleBallotRaw: SampleBallotRaw | null, potentialItem: PotentialSampleBallot | null) => void;
  onChangeAutoScouter?: (autoScouter: AutoScouter) => void;
  partyOptions?: Array<{ label: string; value: string }>;
}

// Editable Cell Component
interface EditableCellProps {
  value: string;
  onChange: (value: string) => void;
  placeholder?: string;
  type?: 'text' | 'number';
  multiline?: boolean;
  style?: React.CSSProperties;
  tabIndex?: number;
  onFocus?: () => void;
  onKeyDown?: (e: React.KeyboardEvent) => void;
  disabled?: boolean;
}

const getCandidateMetadataFields = (candidate?: {
  withdrawn?: boolean;
  info?: {
    email?: string | null;
    phone?: string | null;
    website?: string | null;
    address?: string | null;
  };
}) => {
  const info = candidate?.info;
  const fields = [
    { label: 'Email', value: info?.email },
    { label: 'Phone', value: info?.phone },
    { label: 'Website', value: info?.website },
    { label: 'Address', value: info?.address }
  ];

  const metadataFields = fields
    .filter((field): field is { label: string; value: string } => {
      if (typeof field.value !== 'string') return false;
      const normalizedValue = field.value.trim();
      return normalizedValue !== '' && normalizedValue !== 'string';
    })
    .map(field => ({ ...field, value: field.value.trim() }));

  if (candidate?.withdrawn === true) {
    metadataFields.push({ label: 'Withdrawn', value: 'Yes' });
  }

  return metadataFields;
};

const EditableCell: React.FC<EditableCellProps> = ({ 
  value, 
  onChange, 
  placeholder, 
  type = 'text', 
  multiline = false, 
  style,
  tabIndex,
  onFocus,
  onKeyDown,
  disabled = false
}) => {
  const [isEditing, setIsEditing] = useState(false);
  const [localValue, setLocalValue] = useState(value);
  const inputRef = useRef<HTMLInputElement | HTMLTextAreaElement>(null);

  useEffect(() => {
    setLocalValue(value);
  }, [value]);

  useEffect(() => {
    if (isEditing && inputRef.current) {
      inputRef.current.focus();
      inputRef.current.select();
    }
  }, [isEditing]);

  const handleClick = () => {
    if (disabled) return;
    setIsEditing(true);
    setLocalValue(value);
  };

  const handleFocus = () => {
    if (disabled) return;
    setIsEditing(true);
    setLocalValue(value);
    if (onFocus) onFocus();
  };

  const handleBlur = () => {
    setIsEditing(false);
    onChange(localValue);
  };

  const handleKeyDownInternal = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && !multiline) {
      e.preventDefault();
      setIsEditing(false);
      onChange(localValue);
      // Move to next cell
      if (onKeyDown) onKeyDown(e);
    } else if (e.key === 'Escape') {
      e.preventDefault();
      setIsEditing(false);
      setLocalValue(value);
    } else if (e.key === 'Tab') {
      // Save the value immediately
      onChange(localValue);
      // Don't prevent default - let browser handle Tab naturally
      // The tabIndex values will ensure correct order
      // Exit editing mode after a brief delay to allow Tab to propagate
      setTimeout(() => {
        setIsEditing(false);
      }, 0);
    }
  };

  if (isEditing) {
    return (
      <TextField
        inputRef={inputRef}
        value={localValue}
        onChange={(e) => setLocalValue(e.target.value)}
        onBlur={handleBlur}
        onKeyDown={handleKeyDownInternal}
        placeholder={placeholder}
        type={type}
        multiline={multiline}
        size="small"
        variant="outlined"
        fullWidth
        style={style}
        inputProps={{ 
          style: { padding: '8px' },
          tabIndex: tabIndex
        }}
      />
    );
  }

  return (
    <Typography
      variant="body2"
      onClick={handleClick}
      onFocus={handleFocus}
      tabIndex={disabled ? -1 : tabIndex}
      style={{
        cursor: disabled ? 'default' : 'text',
        padding: '8px',
        minHeight: '32px',
        display: 'flex',
        alignItems: 'center',
        border: '1px solid transparent',
        borderRadius: '4px',
        outline: 'none',
        ...style
      }}
      onMouseEnter={(e) => {
        e.currentTarget.style.backgroundColor = '#f5f5f5';
      }}
      onMouseLeave={(e) => {
        e.currentTarget.style.backgroundColor = 'transparent';
      }}
    >
      {value || <span style={{ color: '#999', fontStyle: 'italic' }}>{placeholder || 'Click to edit'}</span>}
    </Typography>
  );
};

const ScrapedContentItem = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 8px 12px;
  background: #FFFFFF;
  border: 1px solid #E0E0E0;
  border-radius: 4px;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);
  transition: box-shadow 0.2s;
  max-width: 800px;
  
  &:hover {
    box-shadow: 0 2px 6px rgba(0, 0, 0, 0.15);
  }
`;

const ScrapedContentTitle = styled.div`
  flex: 1;
  min-width: 0;
`;

const ScrapedContentStats = styled.div`
  flex-shrink: 0;
  margin: 0 12px;
`;

const ScrapedContentStatus = styled.div`
  flex-shrink: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  width: 24px;
  height: 24px;
`;

const EditBallotDialog = styled(Dialog)`
  & .MuiDialog-paper {
    max-width: 95vw;
    width: 95vw;
    max-height: 95vh;
    height: 95vh;
    margin: 2.5vh auto;
  }
`;

const StyledTableRow = styled(MuiTableRow)<{ $isEven?: boolean }>`
  background-color: ${props => props.$isEven ? '#F9F9F9' : '#FFFFFF'};
  
  &:hover {
    background-color: #F0F0F0;
  }
`;

const ParsedSampleBallotTile: React.FC<SampleBallotTileProps> = ({
  url,
  parseStatus,
  value,
  relevant,
  enableEditing = false,
  disableEditing = false,
  autoScouterId,
  sourceType = 'sample-ballot',
  bodyText,
  errorMessage,
  warningMessage,
  bulkEditMode = false,
  isSelected = false,
  onSelectionChange,
  onRemove,
  removing = false,
  onChangeBallot,
  onChangeAutoScouter,
  partyOptions = []
}) => {
  const feathers = useFeathers<Application<ServiceTypes>>();
  const [memoizedValue, setMemoizedValue] = useState<SampleBallotRaw>(value);
  const [allowValueUpdate, setAllowValueUpdate] = useState(false);
  const raceCount = memoizedValue.races?.length || 0;
  const measureCount = memoizedValue.measures?.length || 0;
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [editingBallotData, setEditingBallotData] = useState<SampleBallotRaw | null>(null);
  const [loadingLatestBallot, setLoadingLatestBallot] = useState(false);
  const [saving, setSaving] = useState(false);
  const [updatingRelevant, setUpdatingRelevant] = useState(false);
  const [bodyTextExpanded, setBodyTextExpanded] = useState(false);
  const [editingBodyText, setEditingBodyText] = useState(false);
  const [localBodyText, setLocalBodyText] = useState(bodyText || '');
  const [reparsing, setReparsing] = useState(false);
  const [cancelling, setCancelling] = useState(false);
  const [reparseDialogOpen, setReparseDialogOpen] = useState(false);
  const [reparsePrompt, setReparsePrompt] = useState('');
  const [hasEditedBallotData, setHasEditedBallotData] = useState(false);
  const [election, setElection] = useState<Election | null>(null);
  const [currentAutoScouter, setCurrentAutoScouter] = useState<AutoScouter | null>(null);
  const [expandedCandidateMetadata, setExpandedCandidateMetadata] = useState<Record<string, boolean>>({});
  const prevModalOpenRef = useRef(false);
  const previousParseStatusRef = useRef(parseStatus);

  useEffect(() => {
    const parseJustCompleted =
      previousParseStatusRef.current === 'in-progress' && parseStatus === 'success';

    if (parseStatus !== 'success' || parseJustCompleted || allowValueUpdate) {
      setMemoizedValue(value);
      if (allowValueUpdate) {
        setAllowValueUpdate(false);
      }
    }

    previousParseStatusRef.current = parseStatus;
  }, [value, parseStatus, allowValueUpdate]);

  const handleBallotChange = (
    sampleBallotRaw: SampleBallotRaw | null,
    potentialItem: PotentialSampleBallot | null
  ) => {
    if (!onChangeBallot) return;
    setAllowValueUpdate(true);
    onChangeBallot(sampleBallotRaw, potentialItem);
  };

  const markBallotDataAsEdited = () => {
    setHasEditedBallotData(true);
  };

  const updateEditingBallotData = (updater: React.SetStateAction<SampleBallotRaw | null>) => {
    markBallotDataAsEdited();
    setEditingBallotData(updater);
  };

  // Reset accordion state only when modal first opens
  useEffect(() => {
    if (isModalOpen && !prevModalOpenRef.current) {
      // Modal just opened - reset accordion state
      setBodyTextExpanded(false);
      setEditingBodyText(false);
      setHasEditedBallotData(false);
    }
    prevModalOpenRef.current = isModalOpen;
  }, [isModalOpen]);

  // Fetch the latest ballot data and election when the modal opens.
  useEffect(() => {
    if (isModalOpen && feathers) {
      let cancelled = false;

      const fetchLatestBallot = async () => {
        try {
          const autoScouter = await feathers.getService('auto-scouter').get(autoScouterId);
          if (cancelled) return;

          setCurrentAutoScouter(autoScouter);
          const latestBallot = autoScouter.sampleBallotsRaw?.find(
            ballot => ballot.url === url
          ) || { url };
          setMemoizedValue(latestBallot);
          setEditingBallotData({
            url: latestBallot.url || url,
            races: latestBallot.races ? [...latestBallot.races] : [],
            measures: latestBallot.measures ? [...latestBallot.measures] : []
          });

          if (onChangeAutoScouter) {
            onChangeAutoScouter(autoScouter);
          }

          if (autoScouter.electionKey) {
            const electionData = await feathers.getService('elections').get(autoScouter.electionKey);
            if (cancelled) return;
            setElection(electionData);
          }
        } catch (error) {
          if (!cancelled) {
            console.error('Error fetching latest ballot data:', error);
          }
        } finally {
          if (!cancelled) {
            setLoadingLatestBallot(false);
          }
        }
      };

      fetchLatestBallot();

      return () => {
        cancelled = true;
      };
    }
  }, [isModalOpen, autoScouterId, feathers]);

  // Update data when props change, but preserve accordion state
  useEffect(() => {
    if (isModalOpen && !loadingLatestBallot) {
      setEditingBallotData({
        url: memoizedValue.url || url,
        races: memoizedValue.races ? [...memoizedValue.races] : [],
        measures: memoizedValue.measures ? [...memoizedValue.measures] : []
      });
      // Only update localBodyText if not currently editing
      if (!editingBodyText) {
        setLocalBodyText(bodyText || '');
      }
    }
  }, [isModalOpen, loadingLatestBallot, memoizedValue, url, bodyText, editingBodyText]);

  const handleSaveBodyText = async (newBodyText: string) => {
    if (!feathers || sourceType === 'sample-ballot') return;

    try {
      const patch: PatchAutoScouter = {
        $reparseContent: {
          contentType: sourceType,
          url: url,
          bodyText: newBodyText
        }
      };
      const updated = await feathers.getService('auto-scouter').patch(autoScouterId, patch);

      if (onChangeBallot) {
        const updatedPotential = updated.potentialSampleBallots?.find(p => p.url === url) || null;
        const updatedRaw = updated.sampleBallotsRaw?.find(r => r.url === url) || null;
        handleBallotChange(updatedRaw, updatedPotential);
      }
    } catch (error) {
      console.error('Error saving body text:', error);
      alert('Error saving body text. Please try again.');
    }
  };

  const handleRelevantStatusChange = async (newRelevant: boolean) => {
    if (!feathers) return;

    setUpdatingRelevant(true);
    try {
      const updated = await feathers.getService('auto-scouter').patch(autoScouterId, {
        $markRelevancy: {
          contentType: sourceType,
          url: url,
          relevant: newRelevant
        }
      }) as AutoScouter;

      if (onChangeBallot) {
        const updatedPotential = updated.potentialSampleBallots?.find(
          p => p.url === url && p.contentType === sourceType
        ) || null;
        const updatedRaw =
          updated.sampleBallotsRaw?.find(r => r.url === url) || null;

        handleBallotChange(updatedRaw, updatedPotential);
      }

      // Notify parent component of the full AutoScouter update
      if (onChangeAutoScouter) {
        onChangeAutoScouter(updated);
      }
    } catch (error) {
      console.error('Error updating relevant status:', error);
      alert('Error updating relevant status. Please try again.');
    } finally {
      setUpdatingRelevant(false);
    }
  };

  const handleSave = async () => {
    if (!editingBallotData || !feathers) return;

    setSaving(true);
    try {
      const updated = await feathers.getService('auto-scouter').patch(autoScouterId, {
        $updateSampleBallotRaw: {
          url: editingBallotData.url || url,
          races: editingBallotData.races || [],
          measures: editingBallotData.measures || []
        }
      }) as AutoScouter;

      if (onChangeBallot) {
        const updatedRaw =
          updated.sampleBallotsRaw?.find(r => r.url === (editingBallotData.url || url)) || null;
        const updatedPotential = updated.potentialSampleBallots?.find(
          p => p.url === (editingBallotData.url || url) && p.contentType === sourceType
        ) || null;

        handleBallotChange(updatedRaw, updatedPotential);
      }

      // Notify parent component of the full AutoScouter update
      if (onChangeAutoScouter) {
        onChangeAutoScouter(updated);
      }

      setIsModalOpen(false);
    } catch (error) {
      console.error('Error saving ballot:', error);
      alert('Error saving ballot. Please try again.');
    } finally {
      setSaving(false);
    }
  };

  const getStatusIcon = () => {
    if (parseStatus === 'error') {
      return (
        <Tooltip title="Parse error">
          <ErrorIcon style={{ color: '#D32F2F', fontSize: '24px' }} />
        </Tooltip>
      );
    }
    if (parseStatus === 'in-progress') {
      return (
        <Tooltip title="Parsing in progress">
          <CircularProgress size={24} />
        </Tooltip>
      );
    }
    if (relevant === false) {
      return (
        <Tooltip title="Not relevant">
          <RemoveCircleOutlineIcon style={{ color: '#9E9E9E', fontSize: '22px' }} />
        </Tooltip>
      );
    }
    if (relevant === true && parseStatus === 'success') {
      return (
        <Tooltip title="Relevant and successfully parsed">
          <CheckCircleIcon style={{ color: '#4CAF50', fontSize: '24px' }} />
        </Tooltip>
      );
    }
    if (parseStatus === 'not-started') {
      return (
        <Tooltip title="Parsing in progress">
          <CircularProgress size={24} />
        </Tooltip>
      );
    }
    return null;
  };

  const handleClick = () => {
    if (enableEditing) {
      setLoadingLatestBallot(true);
      setEditingBallotData(null);
      setIsModalOpen(true);
    }
  };

  const handleOpenReparseDialog = () => {
    setReparsePrompt(PARSE_SAMPLE_BALLOT_INSTRUCTIONS);
    setReparseDialogOpen(true);
  };

  const handleReparse = async () => {
    if (!feathers) return;

    setReparsing(true);
    try {
      // Use auto-scouter patch to set parse-status to 'not-started'
      // This will automatically trigger re-parsing through before hooks
      const updated = await feathers.getService('auto-scouter').patch(autoScouterId, {
        $reparseContent: {
          contentType: sourceType,
          url: url,
          prompt: reparsePrompt
        }
      }) as AutoScouter;

      if (onChangeBallot) {
        const updatedPotential = updated.potentialSampleBallots?.find(
          p => p.url === url && p.contentType === sourceType
        ) || null;
        const updatedRaw = updated.sampleBallotsRaw?.find(r => r.url === url) || null;
        handleBallotChange(updatedRaw, updatedPotential);
      }

      if (onChangeAutoScouter) {
        onChangeAutoScouter(updated);
      }

      setCurrentAutoScouter(updated);
      setReparseDialogOpen(false);
    } catch (error) {
      console.error('Error re-parsing content:', error);
      alert('Error re-parsing content. Please try again.');
    } finally {
      setReparsing(false);
    }
  };

  const handleCancelParse = async () => {
    if (!feathers) return;

    setCancelling(true);
    try {
      const updated = await feathers.getService('auto-scouter').patch(autoScouterId, {
        $cancelParse: {
          contentType: sourceType,
          url: url
        }
      }) as AutoScouter;

      if (onChangeBallot) {
        const updatedPotential = updated.potentialSampleBallots?.find(
          p => p.url === url && p.contentType === sourceType
        ) || null;
        const updatedRaw = updated.sampleBallotsRaw?.find(r => r.url === url) || null;
        handleBallotChange(updatedRaw, updatedPotential);
      }

      if (onChangeAutoScouter) {
        onChangeAutoScouter(updated);
      }
    } catch (error) {
      console.error('Error cancelling parse:', error);
      alert('Error cancelling parse. Please try again.');
    } finally {
      setCancelling(false);
    }
  };

  return (
    <>
      <ScrapedContentItem onClick={bulkEditMode ? undefined : handleClick} style={{ cursor: bulkEditMode ? 'default' : (enableEditing ? 'pointer' : 'default') }}>
        {bulkEditMode && (
          <Checkbox
            checked={isSelected}
            onChange={(e) => {
              e.stopPropagation();
              if (onSelectionChange) {
                onSelectionChange(e.target.checked);
              }
            }}
            onClick={(e) => e.stopPropagation()}
            style={{ padding: '4px' }}
          />
        )}
        <ScrapedContentTitle>
          <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
            <Typography variant="body2" style={{ fontWeight: 500, wordBreak: 'break-all' }}>
              {url}
            </Typography>
            <a 
              href={url} 
              target="_blank" 
              rel="noopener noreferrer"
              style={{ color: '#22034F', display: 'flex', alignItems: 'center' }}
              onClick={(e) => e.stopPropagation()}
            >
              <OpenInNewIcon fontSize="small" />
            </a>
          </div>
          {parseStatus === 'error' && errorMessage && (
            <Typography
              variant="body2"
              color="error"
              style={{ marginTop: '4px', wordBreak: 'break-word' }}
            >
              {errorMessage}
            </Typography>
          )}
          {warningMessage && (
            <Typography
              variant="body2"
              style={{
                color: '#ED6C02',
                marginTop: '4px',
                wordBreak: 'break-word'
              }}
            >
              {warningMessage}
            </Typography>
          )}
        </ScrapedContentTitle>
        {relevant === true && (
          <ScrapedContentStats>
            <Typography variant="body2" color="textSecondary">
              {raceCount > 0 && (
                <>
                  {raceCount} race{raceCount !== 1 ? 's' : ''}
                  {measureCount > 0 && <span style={{ fontSize: '0.75rem' }}> • </span>}
                </>
              )}
              {measureCount > 0 && `${measureCount} measure${measureCount !== 1 ? 's' : ''}`}
            </Typography>
          </ScrapedContentStats>
        )}
        <ScrapedContentStatus>
          {getStatusIcon()}
        </ScrapedContentStatus>
        {onRemove && !bulkEditMode && (
          <Tooltip title="Remove content">
            <span>
              <IconButton
                size="small"
                aria-label={`Remove ${url}`}
                onClick={(event) => {
                  event.stopPropagation();
                  onRemove();
                }}
                disabled={disableEditing || removing}
              >
                {removing ? <CircularProgress size={18} /> : <DeleteIcon fontSize="small" />}
              </IconButton>
            </span>
          </Tooltip>
        )}
      </ScrapedContentItem>

      <EditBallotDialog
        open={isModalOpen}
        onClose={() => setIsModalOpen(false)}
      >
        <DialogTitle>
          <Box display="flex" justifyContent="space-between" alignItems="center">
            <Box display="flex" alignItems="center" gap={1}>
              <Typography variant="h6">
                Edit {sourceType === 'candidate-list' ? 'Candidate List' : sourceType === 'measure-list' ? 'Measure/Proposal List' : 'Sample Ballot'}
              </Typography>
              {(parseStatus === 'in-progress') && (
                <Box display="flex" alignItems="center" gap={0.5}>
                  <Tooltip title="Parsing in progress">
                    <Box display="flex" alignItems="center" justifyContent="center" width={24} height={24}>
                      <CircularProgress size={20} />
                    </Box>
                  </Tooltip>
                  <Tooltip title="Cancel parsing">
                    <IconButton
                      onClick={handleCancelParse}
                      size="small"
                      disabled={cancelling}
                      tabIndex={-1}
                    >
                      {cancelling ? <CircularProgress size={16} /> : <CancelIcon fontSize="small" />}
                    </IconButton>
                  </Tooltip>
                </Box>
              )}
              {parseStatus === 'error' && (
                <Tooltip title="Parse error">
                  <ErrorIcon style={{ color: '#D32F2F', fontSize: '24px' }} />
                </Tooltip>
              )}
            </Box>
            <Box display="flex" alignItems="center" gap={1}>
              {(parseStatus === 'error' || parseStatus === 'success') && (
                <Tooltip title="Re-parse content">
                  <IconButton 
                    onClick={handleOpenReparseDialog}
                    size="small" 
                    disabled={reparsing}
                    tabIndex={-1}
                  >
                    {reparsing ? <CircularProgress size={20} /> : <RefreshIcon />}
                  </IconButton>
                </Tooltip>
              )}
              <IconButton onClick={() => setIsModalOpen(false)} size="small" tabIndex={-1}>
                <CloseIcon />
              </IconButton>
            </Box>
          </Box>
        </DialogTitle>
        <DialogContent style={{ padding: '24px', overflow: 'auto' }}>
          {editingBallotData ? (
            <Box display="flex" flexDirection="column" gap={4}>
              {/* Source PDF + Relevance (inline) */}
              <Box
                mb={2}
                display="flex"
                alignItems="center"
                justifyContent="flex-start"
                gap={6}
              >
                <Box>
                  <Typography variant="body2" color="textSecondary">
                    Source {sourceType === 'sample-ballot' ? 'PDF' : 'URL'}:
                  </Typography>
                  <a 
                    href={url} 
                    target="_blank" 
                    rel="noopener noreferrer"
                    style={{ 
                      color: '#2196F3', 
                      textDecoration: 'underline',
                      wordBreak: 'break-all',
                      display: 'flex',
                      alignItems: 'center',
                      gap: '4px'
                    }}
                  >
                    {url}
                    <OpenInNewIcon fontSize="small" />
                  </a>
                  {/* Body Text Display */}
                  {bodyText && (
                    <Box mt={2} style={{ maxWidth: '600px' }}>
                      <Box
                        display="flex"
                        alignItems="center"
                        gap={1}
                        style={{ cursor: 'pointer' }}
                        onClick={() => setBodyTextExpanded(!bodyTextExpanded)}
                      >
                        {bodyTextExpanded ? <ExpandLessIcon fontSize="small" /> : <ExpandMoreIcon fontSize="small" />}
                        <Typography variant="body2" color="textSecondary" style={{ fontStyle: 'italic' }}>
                          {bodyTextExpanded ? 'Hide body text' : bodyText.split('\n')[0] || 'View body text'}
                        </Typography>
                      </Box>
                      <Collapse in={bodyTextExpanded}>
                        <Box mt={1}>
                          {editingBodyText ? (
                            <TextField
                              fullWidth
                              multiline
                              rows={10}
                              value={localBodyText}
                              onChange={(e) => setLocalBodyText(e.target.value)}
                              onBlur={async () => {
                                setEditingBodyText(false);
                                // Save bodyText if it changed for a list source.
                                if (sourceType !== 'sample-ballot' && localBodyText !== bodyText) {
                                  await handleSaveBodyText(localBodyText);
                                }
                              }}
                              onKeyDown={(e) => {
                                if (e.key === 'Escape') {
                                  setLocalBodyText(bodyText || '');
                                  setEditingBodyText(false);
                                }
                              }}
                              variant="outlined"
                              size="small"
                              autoFocus
                              disabled={disableEditing}
                            />
                          ) : (
                            <Typography
                              variant="body2"
                              style={{
                                whiteSpace: 'pre-wrap',
                                padding: '12px',
                                backgroundColor: '#F5F5F5',
                                borderRadius: '4px',
                                cursor: disableEditing ? 'default' : 'text',
                                minHeight: '100px'
                              }}
                              onClick={() => !disableEditing && setEditingBodyText(true)}
                            >
                              {localBodyText || bodyText}
                            </Typography>
                          )}
                        </Box>
                      </Collapse>
                    </Box>
                  )}
                </Box>
                <Box display="flex" flexDirection="column">
                  <Typography variant="body2" color="textSecondary">
                    Relevance:
                  </Typography>
                  <Select
                    value={
                      parseStatus === 'error'
                        ? 'error'
                        : (relevant === true ? 'relevant' : relevant === false ? 'not-relevant' : '')
                    }
                    onChange={() => {
                      // no-op; we trigger updates from MenuItem onClick
                    }}
                    disabled={updatingRelevant || disableEditing}
                    size="small"
                    displayEmpty
                    style={{ minWidth: '180px' }}
                  >
                    <MenuItem value="" disabled>
                      <em>Select relevance</em>
                    </MenuItem>
                    <MenuItem
                      value="error"
                      style={{ color: '#D32F2F', fontWeight: 600 }}
                    >
                      Error
                    </MenuItem>
                    <MenuItem
                      value="relevant"
                      onClick={() => handleRelevantStatusChange(true)}
                    >
                      Relevant
                    </MenuItem>
                    <MenuItem
                      value="not-relevant"
                      onClick={() => handleRelevantStatusChange(false)}
                    >
                      Not relevant
                    </MenuItem>
                  </Select>
                  {updatingRelevant && (
                    <CircularProgress size={16} style={{ marginLeft: '4px' }} />
                  )}
                </Box>
              </Box>

              {/* Races Section */}
              <Box>
                <Box display="flex" alignItems="center" gap={1} mb={2}>
                  <Typography variant="h6">Races</Typography>
                  <Typography variant="body2" color="textSecondary">
                    ({editingBallotData.races?.length || 0})
                  </Typography>
                </Box>
                <TableContainer component={Paper} variant="outlined">
                  <Table size="small">
                    <TableHead>
                      <MuiTableRow>
                        <TableCell style={{ width: '25%' }}>Race Name</TableCell>
                        <TableCell style={{ width: '20%' }}>District Heading</TableCell>
                        <TableCell style={{ width: '12%' }}>Vote Count</TableCell>
                        {election?.partisan === true && (
                          <TableCell style={{ width: '12%' }}>Race Party</TableCell>
                        )}
                        <TableCell style={{ width: election?.partisan === true ? '20%' : '25%' }}>Candidates</TableCell>
                        <TableCell style={{ width: '5%' }}></TableCell>
                      </MuiTableRow>
                    </TableHead>
                    <TableBody>
                      {editingBallotData.races?.map((race, raceIndex) => {
                        // Calculate sequential tabIndex accounting for all previous races and their candidates
                        let currentTabIndex = 1; // Start at 1
                        const fieldsPerRace = election?.partisan === true ? 4 : 3; // Race name, district heading, vote count, (raceParty if partisan)
                        
                        // Add tabIndex for all previous races
                        for (let i = 0; i < raceIndex; i++) {
                          const prevRace = editingBallotData.races?.[i];
                          currentTabIndex += fieldsPerRace; // Race name, district heading, vote count, (raceParty if partisan)
                          if (prevRace?.candidates) {
                            currentTabIndex += prevRace.candidates.length * 2; // Name + party per candidate
                          }
                        }
                        
                        // Now set tabIndex for current race fields
                        const raceNameTabIndex = currentTabIndex++;
                        const districtHeadingTabIndex = currentTabIndex++;
                        const voteCountTabIndex = currentTabIndex++;
                        const racePartyTabIndex = election?.partisan === true ? currentTabIndex++ : null;
                        
                        return (
                          <StyledTableRow key={raceIndex} $isEven={raceIndex % 2 === 0}>
                            <TableCell>
                              <EditableCell
                                value={race.raceName || ''}
                                onChange={(value) => {
                                  updateEditingBallotData(prev => {
                                    const newRaces = [...(prev?.races || [])];
                                    newRaces[raceIndex] = { ...newRaces[raceIndex], raceName: value };
                                    return { ...prev!, races: newRaces };
                                  });
                                }}
                                placeholder="Race name"
                                tabIndex={raceNameTabIndex}
                                disabled={disableEditing}
                              />
                            </TableCell>
                            <TableCell>
                              <EditableCell
                                value={race.districtHeading || ''}
                                onChange={(value) => {
                                  updateEditingBallotData(prev => {
                                    const newRaces = [...(prev?.races || [])];
                                    newRaces[raceIndex] = { ...newRaces[raceIndex], districtHeading: value };
                                    return { ...prev!, races: newRaces };
                                  });
                                }}
                                placeholder="District heading"
                                tabIndex={districtHeadingTabIndex}
                                disabled={disableEditing}
                              />
                            </TableCell>
                            <TableCell>
                              <EditableCell
                                value={race.voteCount?.toString() || ''}
                                onChange={(value) => {
                                  const numValue = value === '' ? undefined : parseInt(value, 10);
                                  if (numValue !== undefined && isNaN(numValue)) return;
                                  updateEditingBallotData(prev => {
                                    const newRaces = [...(prev?.races || [])];
                                    newRaces[raceIndex] = { ...newRaces[raceIndex], voteCount: numValue };
                                    return { ...prev!, races: newRaces };
                                  });
                                }}
                                placeholder="Vote count"
                                type="number"
                                tabIndex={voteCountTabIndex}
                                disabled={disableEditing}
                              />
                            </TableCell>
                            {election?.partisan === true && (
                              <TableCell>
                                <Select
                                  value={race.raceParty || ''}
                                  onChange={(e) => {
                                    updateEditingBallotData(prev => {
                                      const newRaces = [...(prev?.races || [])];
                                      newRaces[raceIndex] = { ...newRaces[raceIndex], raceParty: e.target.value || undefined };
                                      return { ...prev!, races: newRaces };
                                    });
                                  }}
                                  size="small"
                                  displayEmpty
                                  style={{ minWidth: '100px', fontSize: '0.875rem' }}
                                  tabIndex={racePartyTabIndex || undefined}
                                  disabled={disableEditing}
                                >
                                  <MenuItem value="">
                                    <em>-</em>
                                  </MenuItem>
                                  {partyOptions.map((option) => (
                                    <MenuItem key={option.value} value={option.value}>
                                      {option.label}
                                    </MenuItem>
                                  ))}
                                </Select>
                              </TableCell>
                            )}
                            <TableCell>
                              <Box display="flex" flexDirection="column" gap={0.5}>
                                {race.candidates?.map((candidate, candidateIndex) => {
                                  const candidateNameTabIndex = currentTabIndex++;
                                  const candidatePartyTabIndex = currentTabIndex++;
                                  const metadataFields = getCandidateMetadataFields(candidate);
                                  const metadataCount = metadataFields.length;
                                  const metadataKey = `${raceIndex}-${candidateIndex}`;
                                  const isMetadataExpanded = Boolean(expandedCandidateMetadata[metadataKey]);
                                  return (
                                    <Box key={candidateIndex} display="flex" alignItems="center" gap={1}>
                                      <Box style={{ flex: 1, display: 'flex', flexDirection: 'column', gap: '2px' }}>
                                        <EditableCell
                                          value={candidate.name || ''}
                                          onChange={(value) => {
                                            updateEditingBallotData(prev => {
                                              const newRaces = [...(prev?.races || [])];
                                              const newCandidates = [...(newRaces[raceIndex].candidates || [])];
                                              newCandidates[candidateIndex] = { ...newCandidates[candidateIndex], name: value };
                                              newRaces[raceIndex] = { ...newRaces[raceIndex], candidates: newCandidates };
                                              return { ...prev!, races: newRaces };
                                            });
                                          }}
                                          placeholder="Candidate name"
                                          style={{ flex: 1 }}
                                          tabIndex={candidateNameTabIndex}
                                          disabled={disableEditing}
                                        />
                                        {sourceType !== 'sample-ballot' && metadataCount > 0 && (
                                          <>
                                            <Box
                                              component="button"
                                              type="button"
                                              aria-expanded={isMetadataExpanded}
                                              onClick={() => {
                                                setExpandedCandidateMetadata(previous => ({
                                                  ...previous,
                                                  [metadataKey]: !previous[metadataKey]
                                                }));
                                              }}
                                              style={{
                                                alignItems: 'center',
                                                alignSelf: 'flex-start',
                                                background: 'none',
                                                border: 0,
                                                color: 'rgba(0, 0, 0, 0.6)',
                                                cursor: 'pointer',
                                                display: 'flex',
                                                font: 'inherit',
                                                fontSize: '0.68rem',
                                                marginLeft: '8px',
                                                marginTop: '-16px',
                                                padding: 0
                                              }}
                                            >
                                              {isMetadataExpanded
                                                ? <ExpandLessIcon style={{ fontSize: '0.9rem' }} />
                                                : <ExpandMoreIcon style={{ fontSize: '0.9rem' }} />}
                                              {metadataCount} metadata field{metadataCount !== 1 ? 's' : ''}
                                            </Box>
                                            <Collapse in={isMetadataExpanded} timeout="auto" unmountOnExit>
                                              <Box
                                                display="flex"
                                                flexDirection="column"
                                                gap={0.25}
                                                mt={0.5}
                                                ml={1}
                                                p={1}
                                                style={{ backgroundColor: '#F5F5F5', borderRadius: '4px' }}
                                              >
                                                {metadataFields.map(field => (
                                                  <Typography
                                                    key={field.label}
                                                    variant="caption"
                                                    style={{ fontSize: '0.68rem', overflowWrap: 'anywhere' }}
                                                  >
                                                    <strong>{field.label}:</strong> {field.value}
                                                  </Typography>
                                                ))}
                                              </Box>
                                            </Collapse>
                                          </>
                                        )}
                                      </Box>
                                      <Select
                                        value={candidate.party || ''}
                                        onChange={(e) => {
                                          updateEditingBallotData(prev => {
                                            const newRaces = [...(prev?.races || [])];
                                            const newCandidates = [...(newRaces[raceIndex].candidates || [])];
                                            newCandidates[candidateIndex] = { ...newCandidates[candidateIndex], party: e.target.value };
                                            newRaces[raceIndex] = { ...newRaces[raceIndex], candidates: newCandidates };
                                            return { ...prev!, races: newRaces };
                                          });
                                        }}
                                        size="small"
                                        displayEmpty
                                        style={{ minWidth: '120px' }}
                                        tabIndex={candidatePartyTabIndex}
                                        disabled={disableEditing}
                                      >
                                        <MenuItem value="">None</MenuItem>
                                        {partyOptions.map((option) => (
                                          <MenuItem key={option.value} value={option.value}>
                                            {option.label}
                                          </MenuItem>
                                        ))}
                                      </Select>
                                      <IconButton
                                        size="small"
                                        onClick={() => {
                                          updateEditingBallotData(prev => {
                                            const newRaces = [...(prev?.races || [])];
                                            const newCandidates = (newRaces[raceIndex].candidates || []).filter((_, i) => i !== candidateIndex);
                                            newRaces[raceIndex] = { ...newRaces[raceIndex], candidates: newCandidates };
                                            return { ...prev!, races: newRaces };
                                          });
                                        }}
                                        tabIndex={-1}
                                        disabled={disableEditing}
                                      >
                                        <DeleteIcon fontSize="small" />
                                      </IconButton>
                                    </Box>
                                  );
                                })}
                                <Button
                                  size="small"
                                  startIcon={<AddIcon />}
                                  onClick={() => {
                                    updateEditingBallotData(prev => {
                                      const newRaces = [...(prev?.races || [])];
                                      const newCandidates = [...(newRaces[raceIndex].candidates || []), { name: '', party: '', withdrawn: false, disqualified: false }];
                                      newRaces[raceIndex] = { ...newRaces[raceIndex], candidates: newCandidates };
                                      return { ...prev!, races: newRaces };
                                    });
                                  }}
                                  tabIndex={-1}
                                  disabled={disableEditing}
                                >
                                  Add Candidate
                                </Button>
                              </Box>
                            </TableCell>
                            <TableCell>
                              <IconButton
                                size="small"
                                onClick={() => {
                                  updateEditingBallotData(prev => {
                                    const newRaces = (prev?.races || []).filter((_, i) => i !== raceIndex);
                                    return { ...prev!, races: newRaces };
                                  });
                                }}
                                tabIndex={-1}
                                disabled={disableEditing}
                              >
                                <DeleteIcon fontSize="small" />
                              </IconButton>
                            </TableCell>
                          </StyledTableRow>
                        );
                      })}
                      {(!editingBallotData.races || editingBallotData.races.length === 0) && (
                        <MuiTableRow>
                          <TableCell colSpan={5} align="center" style={{ color: '#999', padding: '24px' }}>
                            No races found.
                          </TableCell>
                        </MuiTableRow>
                      )}
                    </TableBody>
                  </Table>
                </TableContainer>
                <Box mt={2} display="flex" justifyContent="flex-start">
                  <Button
                    startIcon={<AddIcon />}
                    variant="outlined"
                    size="small"
                    onClick={() => {
                      updateEditingBallotData(prev => ({
                        ...prev!,
                        races: [...(prev?.races || []), { raceName: '', districtHeading: '', voteCount: 0, candidates: [] }]
                      }));
                    }}
                    tabIndex={-1}
                    disabled={disableEditing}
                  >
                    Add Race
                  </Button>
                </Box>
              </Box>

              {/* Measures Section */}
              <Box>
                <Box display="flex" alignItems="center" gap={1} mb={2}>
                  <Typography variant="h6">Measures</Typography>
                  <Typography variant="body2" color="textSecondary">
                    ({editingBallotData.measures?.length || 0})
                  </Typography>
                </Box>
                <TableContainer component={Paper} variant="outlined">
                  <Table size="small">
                    <TableHead>
                      <MuiTableRow>
                        <TableCell style={{ width: '30%' }}>Measure Name</TableCell>
                        <TableCell style={{ width: '25%' }}>District Heading</TableCell>
                        <TableCell style={{ width: '40%' }}>Ballot Text</TableCell>
                        <TableCell style={{ width: '5%' }}></TableCell>
                      </MuiTableRow>
                    </TableHead>
                    <TableBody>
                      {(() => {
                        // Calculate starting tabIndex for measures (after all races)
                        let measuresStartTabIndex = 1;
                        if (editingBallotData.races) {
                          editingBallotData.races.forEach(race => {
                            measuresStartTabIndex += 3; // Race name, district heading, vote count
                            if (race.candidates) {
                              measuresStartTabIndex += race.candidates.length * 2; // Name + party per candidate
                            }
                          });
                        }
                        
                        return editingBallotData.measures?.map((measure, measureIndex) => {
                          const measureNameTabIndex = measuresStartTabIndex + (measureIndex * 3);
                          const measureDistrictTabIndex = measuresStartTabIndex + (measureIndex * 3) + 1;
                          const measureBallotTextTabIndex = measuresStartTabIndex + (measureIndex * 3) + 2;
                          
                          return (
                            <StyledTableRow key={measureIndex} $isEven={measureIndex % 2 === 0}>
                              <TableCell>
                                <EditableCell
                                  value={measure.measureName || ''}
                                  onChange={(value) => {
                                    updateEditingBallotData(prev => {
                                      const newMeasures = [...(prev?.measures || [])];
                                      newMeasures[measureIndex] = { ...newMeasures[measureIndex], measureName: value };
                                      return { ...prev!, measures: newMeasures };
                                    });
                                  }}
                                  placeholder="Measure name"
                                  tabIndex={measureNameTabIndex}
                                  disabled={disableEditing}
                                />
                              </TableCell>
                              <TableCell>
                                <EditableCell
                                  value={measure.districtHeading || ''}
                                  onChange={(value) => {
                                    updateEditingBallotData(prev => {
                                      const newMeasures = [...(prev?.measures || [])];
                                      newMeasures[measureIndex] = { ...newMeasures[measureIndex], districtHeading: value };
                                      return { ...prev!, measures: newMeasures };
                                    });
                                  }}
                                  placeholder="District heading"
                                  tabIndex={measureDistrictTabIndex}
                                  disabled={disableEditing}
                                />
                              </TableCell>
                              <TableCell>
                                <EditableCell
                                  value={measure.ballotText || ''}
                                  onChange={(value) => {
                                    updateEditingBallotData(prev => {
                                      const newMeasures = [...(prev?.measures || [])];
                                      newMeasures[measureIndex] = { ...newMeasures[measureIndex], ballotText: value };
                                      return { ...prev!, measures: newMeasures };
                                    });
                                  }}
                                  placeholder="Ballot text"
                                  multiline
                                  tabIndex={measureBallotTextTabIndex}
                                  disabled={disableEditing}
                                />
                              </TableCell>
                              <TableCell>
                                <IconButton
                                  size="small"
                                  onClick={() => {
                                    updateEditingBallotData(prev => {
                                      const newMeasures = (prev?.measures || []).filter((_, i) => i !== measureIndex);
                                      return { ...prev!, measures: newMeasures };
                                    });
                                  }}
                                  tabIndex={-1}
                                >
                                  <DeleteIcon fontSize="small" />
                                </IconButton>
                              </TableCell>
                            </StyledTableRow>
                          );
                        }) || [];
                      })()}
                      {(!editingBallotData.measures || editingBallotData.measures.length === 0) && (
                        <MuiTableRow>
                          <TableCell colSpan={4} align="center" style={{ color: '#999', padding: '24px' }}>
                            No measures found.
                          </TableCell>
                        </MuiTableRow>
                      )}
                    </TableBody>
                  </Table>
                </TableContainer>
                <Box mt={2} display="flex" justifyContent="flex-start">
                  <Button
                    startIcon={<AddIcon />}
                    variant="outlined"
                    size="small"
                    onClick={() => {
                      updateEditingBallotData(prev => ({
                        ...prev!,
                        measures: [...(prev?.measures || []), { measureName: '', districtHeading: '', ballotText: '' }]
                      }));
                    }}
                    tabIndex={-1}
                    disabled={disableEditing}
                  >
                    Add Measure
                  </Button>
                </Box>
              </Box>
            </Box>
          ) : (
            <Typography variant="body1" style={{ padding: '24px' }}>
              Loading ballot data...
            </Typography>
          )}
        </DialogContent>
        {hasEditedBallotData && (
          <DialogActions>
            <Button onClick={() => setIsModalOpen(false)}>
              Cancel
            </Button>
            <Button
              onClick={handleSave}
              variant="contained"
              disabled={saving || disableEditing}
            >
              {saving ? 'Saving...' : 'Save'}
            </Button>
          </DialogActions>
        )}
      </EditBallotDialog>

      <Dialog
        open={reparseDialogOpen}
        onClose={() => !reparsing && setReparseDialogOpen(false)}
        fullWidth
        maxWidth="md"
      >
        <DialogTitle>
          Re-parse Content Prompt
        </DialogTitle>
        <DialogContent>
          <Box display="flex" flexDirection="column" gap={2} mt={1}>
            <Typography variant="body2" color="textSecondary">
              Confirm the prompt that will be used for this re-parse. The prompt is saved as the last-used prompt for this source.
            </Typography>
            <Typography variant="caption" color="textSecondary">
              Source: {url}
            </Typography>
            <PromptEditor
              value={reparsePrompt}
              onChange={setReparsePrompt}
              placeholder="Update or override the parse instructions for this item. You can reference {stateName}, {districtName}, and {electionName}."
              rows={10}
            />
            <Typography variant="caption" color="textSecondary">
              The current event-bus parse instructions have been prefilled.
            </Typography>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setReparseDialogOpen(false)} disabled={reparsing}>
            Cancel
          </Button>
          <Button onClick={handleReparse} variant="contained" disabled={reparsing || !reparsePrompt.trim()}>
            {reparsing ? 'Re-parsing...' : 'Re-parse'}
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default ParsedSampleBallotTile;
